import React, { useEffect, useRef, useState } from "react";
import useTitanApp from "../../../../../hooks/useTitanApp";
import {
  setPatientSummary,
  setIsSummaryGenerated,
  addupdateConsultationSummary,
  setSelectedPatientConsultation,
  setDemDxSummary,
  setDemDxSummaryText,
  updateConsultationButtonStatus,
} from "../../../../../dispatchers/patientDispatchers";
import { setStreamedText } from "../../../../../dispatchers/templateDispatchers";
import generateSummary from "../../../../../utils/SummaryCreation";
import { ConsultationButtonStatus } from "../../../../../enum/ConsultationButtonStatus";
import { setConsultationLoadError } from "../../../../../dispatchers/appDispatchers";

function PatientSummary({ getData, darkMode, highlightedSnomedEvidence }) {
  const { patientState, templateState } = useTitanApp();
  const { selectedTemplate, streamedText } = templateState || {};
  const {
    patientSummary,
    consultationId,
    selectedPatientConsultation,
    isSummaryStreaming,
    isConsultationEnabled,
    isSummaryGenerated,
    consultationStopAfterRefresh,
    demDxSummary,
    isDemDxActive,
    transcript,
    snomedCodes = [],
  } = patientState || {};

  const serviceSummary =
    selectedTemplate?.consultationSummary ??
    selectedPatientConsultation?.template?.consultationSummary;

  const formatSummaryText = (text) => {
    if (!text) return text;
    // Only format if it has escaped newlines
    if (typeof text === "string" && text.includes("\\n")) {
      return text.replace(/\\n/g, "\n").replace(/^["']|["']$/g, "");
    }
    return text;
  };
  const handleUpdateButtonStatus = async () => {
    try {
      const data = {
        consultationId: consultationId,
        buttonStatus: ConsultationButtonStatus.Completed,
      };
      await updateConsultationButtonStatus(data);
    } catch (error) {
      console.error("Error in handleUpdateButtonStatus:", error);
      setConsultationLoadError(true);
    }
  };

  const renderHighlightedText = (text, highlightText) => {
    const formattedText = formatSummaryText(text);

    const tokenizeWordsWithPositions = (value) => {
      const tokens = [];
      const wordRegex = /[a-z0-9]+/gi;
      let match;

      while ((match = wordRegex.exec(value)) !== null) {
        tokens.push({
          word: match[0].toLowerCase(),
          start: match.index,
          end: match.index + match[0].length,
        });
      }

      return tokens;
    };

    const findWordSequenceRange = (sourceText, targetText) => {
      const sourceTokens = tokenizeWordsWithPositions(sourceText);
      const targetTokens = tokenizeWordsWithPositions(targetText);

      if (sourceTokens.length === 0 || targetTokens.length === 0) {
        return null;
      }

      for (let i = 0; i <= sourceTokens.length - targetTokens.length; i += 1) {
        let isMatch = true;

        for (let j = 0; j < targetTokens.length; j += 1) {
          if (sourceTokens[i + j].word !== targetTokens[j].word) {
            isMatch = false;
            break;
          }
        }

        if (isMatch) {
          return {
            start: sourceTokens[i].start,
            end: sourceTokens[i + targetTokens.length - 1].end,
          };
        }
      }

      return null;
    };

    if (!highlightText || !formattedText) {
      return formattedText;
    }

    const matchedRange = findWordSequenceRange(formattedText, highlightText);

    if (!matchedRange) return formattedText;

    const before = formattedText.slice(0, matchedRange.start);
    const match = formattedText.slice(matchedRange.start, matchedRange.end);
    const after = formattedText.slice(matchedRange.end);

    return (
      <>
        {before}
        <mark
          data-snomed-highlight
          className={`rounded px-0.5 transition-colors duration-300 ${
            darkMode ? "bg-gray-600 text-white" : "bg-gray-900 text-white"
          }`}
        >
          {match}
        </mark>
        {after}
      </>
    );
  };

  useEffect(() => {
    const updateSummary = async () => {
      if (demDxSummary !== "") {
        try {
          await addupdateConsultationSummary({
            consultationId: consultationId,
            summary: generateSummary(demDxSummary),
          });
          await handleUpdateButtonStatus();
        } catch (error) {
          console.error("Error updating consultation summary:", error);
        }
      }
    };

    updateSummary();
  }, [demDxSummary]);

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (consultationStopAfterRefresh) return;

    const fetchData = async () => {
      if (
        !isSummaryGenerated &&
        transcript !== "" &&
        serviceSummary !== "" &&
        !isDemDxActive &&
        selectedPatientConsultation === null &&
        !hasFetchedRef.current
      ) {
        await getData();
        setIsSummaryGenerated(true);

        if (!consultationStopAfterRefresh) {
          hasFetchedRef.current = true;
        }
      } else if (patientSummary) {
        if (patientSummary.SummaryText) {
          setStreamedText(patientSummary.SummaryText);
        } else {
          const summaryText = Object.entries(patientSummary)
            .filter(
              ([k]) =>
                k !== "Actions" && k !== "SummaryText" && k !== "snomedCodes",
            )
            .map(([k, v]) => `${k}: \n${v}`)
            .join("\n\n");
          setStreamedText(summaryText);
        }
      }

      if (selectedPatientConsultation != null && streamedText === "") {
        try {
          setStreamedText(
            formatSummaryText(selectedPatientConsultation?.consultationSummary),
          );
        } catch (err) {
          console.warn("Invalid consultationSummary JSON:", err);
        }
      }
    };

    fetchData();
  }, [patientSummary, transcript]);

  useEffect(() => {
    const fetchData = async () => {
      if (hasFetchedRef.current) return;

      if (
        selectedPatientConsultation?.consultationButtonStatus ===
          ConsultationButtonStatus.Manual &&
        !isSummaryGenerated
      ) {
        await getData(); // ✅ async call
        setIsSummaryGenerated(true);
        hasFetchedRef.current = true;
        return;
      }

      if (
        selectedPatientConsultation?.consultationButtonStatus ===
        ConsultationButtonStatus.Completed
      ) {
        return;
      }

      if (
        !isSummaryGenerated &&
        transcript !== "" &&
        serviceSummary !== "" &&
        !isDemDxActive
      ) {
        await getData(); // ✅ async call
        setIsSummaryGenerated(true);
        hasFetchedRef.current = true;
      }
    };

    fetchData();
  }, [consultationStopAfterRefresh]);

  const lastVisibleRef = useRef("");
  let visibleText =
    streamedText && String(streamedText).trim() !== ""
      ? streamedText
      : patientSummary?.SummaryText &&
          String(patientSummary.SummaryText).trim() !== ""
        ? patientSummary.SummaryText
        : lastVisibleRef.current || (isSummaryStreaming ? "" : " ");

  if (visibleText && String(visibleText).trim() !== "") {
    lastVisibleRef.current = visibleText;
  }

  return (
    <div className="pc-tabbed-content patient-template-wrapper summary-container">
      <div className="section-title">
        <h5>Consultation Summary</h5>
      </div>
      {demDxSummary === "" ? (
        <div className="summary-section">
          {isSummaryStreaming && streamedText === "" && (
            <p className="blinking-dot-ai">⬤</p>
          )}
          <p
            data-snomed-highlight
            className="summary-editable full-summary"
            contentEditable={!isConsultationEnabled}
            suppressContentEditableWarning
            onBlur={(e) => {
              if (selectedPatientConsultation?.consultationStatus === 1) return;
              const text = e.target.innerText.trim();
              const updatedSummary = {
                ...patientSummary,
                SummaryText: text,
              };
              addupdateConsultationSummary({
                consultationId: consultationId,
                summary:
                  JSON.stringify(updatedSummary?.SummaryText) +
                  " SnomedCodes: " +
                  JSON.stringify(snomedCodes),
              });
              setPatientSummary(updatedSummary);
              setSelectedPatientConsultation({
                ...selectedPatientConsultation,
                consultationSummary: text,
              });
              setStreamedText(text);
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {renderHighlightedText(visibleText, highlightedSnomedEvidence)}
          </p>
        </div>
      ) : (
        <div className="summary-section">
          <p
            className="summary-editable full-summary"
            contentEditable={!isConsultationEnabled}
            onBlur={(e) => {
              if (selectedPatientConsultation?.consultationStatus === 1) return;
              const text = e.target.innerText.trim();
              const updatedSummary = {
                ...demDxSummary,
                SummaryText: text,
              };
              addupdateConsultationSummary({
                consultationId: consultationId,
                summary: JSON.stringify(updatedSummary?.SummaryText),
              });
              setSelectedPatientConsultation({
                ...selectedPatientConsultation,
                consultationSummary: text,
              });
              setDemDxSummaryText(text);
              setDemDxSummary(demDxSummary);
              setStreamedText(text);
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {generateSummary(demDxSummary)}{" "}
          </p>
        </div>
      )}
    </div>
  );
}

export default PatientSummary;
